// Materials to order — auto-generated from accepted quotes (Phase 2).
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Copy, Layers, PencilRuler, PhoneCall, Trash2 } from "lucide-react";

const STATUS_STYLE = {
  to_order: "border-amber-300 bg-amber-50 text-amber-800",
  ordered: "border-cyan-300 bg-cyan-50 text-cyan-800",
  received: "border-emerald-300 bg-emerald-50 text-emerald-800",
  cancelled: "border-slate-300 bg-slate-50 text-slate-500",
};
const NEXT = { to_order: ["ordered", "Mark ordered"], ordered: ["received", "Mark received"] };

export default function MaterialOrders() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [combined, setCombined] = useState(false);
  const { data: orders = [] } = useQuery({
    queryKey: ["material-orders"],
    queryFn: async () => (await api.get("/material-orders")).data,
  });
  const patch = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/material-orders/${id}`, { status })).data,
    onSuccess: (d) => { toast.success(`Order marked ${d.status.replace("_", " ")}`); qc.invalidateQueries({ queryKey: ["material-orders"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed"),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/material-orders/${id}`),
    onSuccess: () => { toast.success("Order removed"); qc.invalidateQueries({ queryKey: ["material-orders"] }); },
  });
  const shown = orders.filter((o) => filter === "all" || o.status === filter);

  // Combined supplier order — aggregate every outstanding line across jobs
  const toOrder = orders.filter((o) => o.status === "to_order");
  const combinedRows = Object.values(
    toOrder.flatMap((o) => (o.items || []).map((i) => ({ ...i, quote: o.quote_number })))
      .reduce((acc, i) => {
        const k = `${i.item}|${i.unit}`;
        if (!acc[k]) acc[k] = { item: i.item, unit: i.unit, qty: 0, quotes: new Set() };
        acc[k].qty += Number(i.qty) || 0;
        acc[k].quotes.add(i.quote);
        return acc;
      }, {})
  ).map((r) => ({ ...r, quotes: [...r.quotes] })).sort((a, b) => a.item.localeCompare(b.item));

  const copyCombined = () => {
    const text = ["CCFI — combined supplier order", ""]
      .concat(combinedRows.map((r) => `${r.qty} ${r.unit} — ${r.item}  (${r.quotes.join(", ")})`))
      .join("\n");
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Order list copied — paste into your email or read it over the phone"))
      .catch(() => toast.error("Copy failed — select and copy manually"));
  };

  return (
    <div data-testid="material-orders-page">
      <PageHeader title="Materials to order" subtitle="Auto-generated take-offs from accepted quotes — ring the supplier, then track ordered → received." />
      <div className="p-4 sm:p-6 space-y-4 max-w-5xl">
        <div className="flex gap-1.5 flex-wrap items-center">
          {["all", "to_order", "ordered", "received"].map((f) => (
            <button key={f} onClick={() => { setFilter(f); setCombined(false); }} data-testid={`orders-filter-${f}`}
              className={`text-xs px-3 py-1.5 rounded-sm border font-medium capitalize ${filter === f && !combined ? "bg-cyan-600 text-white border-cyan-600" : "border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
              {f.replace("_", " ")}{f !== "all" && ` (${orders.filter((o) => o.status === f).length})`}
            </button>
          ))}
          <button onClick={() => setCombined(!combined)} data-testid="orders-combined-toggle"
            className={`text-xs px-3 py-1.5 rounded-sm border font-semibold flex items-center gap-1.5 ${combined ? "bg-amber-500 text-white border-amber-500" : "border-amber-400 text-amber-700 hover:bg-amber-50"}`}>
            <Layers size={12} /> Combined supplier order
          </button>
        </div>
        {combined && (
          <Card className="rounded-sm industrial-shadow" data-testid="combined-order-card">
            <div className="p-4 flex items-center gap-3 flex-wrap border-b border-slate-100">
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">One call, everything outstanding</div>
                <div className="text-xs text-slate-500 mt-0.5">{combinedRows.length} unique items across {toOrder.length} job order{toOrder.length === 1 ? "" : "s"} still to order</div>
              </div>
              <Button size="sm" onClick={copyCombined} disabled={!combinedRows.length}
                className="rounded-sm h-8 bg-amber-500 hover:bg-amber-600 text-white" data-testid="combined-copy-btn">
                <Copy size={12} className="mr-1.5" /> Copy order list
              </Button>
            </div>
            {combinedRows.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-8">Nothing outstanding — everything's been ordered.</p>
            ) : (
              <div className="px-4 py-3">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-400 uppercase tracking-wider text-[10px]">
                    <th className="py-1.5">Total qty</th><th>Item</th><th>For quotes</th>
                  </tr></thead>
                  <tbody>
                    {combinedRows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-50" data-testid={`combined-row-${i}`}>
                        <td className="py-1.5 font-mono font-bold text-slate-900 whitespace-nowrap">{r.qty} {r.unit}</td>
                        <td className="font-medium text-slate-800">{r.item}</td>
                        <td className="text-slate-400">{r.quotes.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}
        {!combined && shown.length === 0 && (
          <Card className="rounded-sm p-10 text-center text-sm text-slate-400">
            Nothing here yet — when a client accepts a quote, the material take-off lands here automatically with a call task.
          </Card>
        )}
        {!combined && shown.map((o) => {
          const next = NEXT[o.status];
          const isOpen = openId === o.id;
          return (
            <Card key={o.id} className="rounded-sm industrial-shadow" data-testid={`material-order-${o.id}`}>
              <div className="p-4 flex items-center gap-3 flex-wrap cursor-pointer" onClick={() => setOpenId(isOpen ? null : o.id)}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{o.quote_number} — {o.customer_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {o.items?.length || 0} lines · steel {o.totals?.steel_lm || 0} LM · aluminium {o.totals?.aluminium_lm || 0} LM · {o.totals?.posts || 0} posts
                    {o.job_number && <> · job {o.job_number}</>}
                  </div>
                </div>
                <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${STATUS_STYLE[o.status] || ""}`}>{(o.status || "").replace("_", " ")}</Badge>
                {next && (
                  <Button size="sm" onClick={(e) => { e.stopPropagation(); patch.mutate({ id: o.id, status: next[0] }); }}
                    className="rounded-sm h-8 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={`order-advance-${o.id}`}>
                    <PhoneCall size={12} className="mr-1.5" /> {next[1]}
                  </Button>
                )}
                {(o.blueprint_ids || []).length > 0 && (
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); nav(`/blueprints?id=${o.blueprint_ids[0]}`); }}
                    className="rounded-sm h-8" data-testid={`order-blueprint-${o.id}`}>
                    <PencilRuler size={12} className="mr-1.5" /> Blueprint
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); window.confirm("Remove this order?") && del.mutate(o.id); }}
                  className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-rose-600" data-testid={`order-delete-${o.id}`}>
                  <Trash2 size={13} />
                </Button>
                {isOpen ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
              </div>
              {isOpen && (
                <div className="border-t border-slate-100 px-4 py-3" data-testid={`order-items-${o.id}`}>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-400 uppercase tracking-wider text-[10px]">
                      <th className="py-1.5">Item</th><th>Qty</th><th>Unit</th><th>Notes</th><th>Section</th>
                    </tr></thead>
                    <tbody>
                      {(o.items || []).map((i, idx) => (
                        <tr key={idx} className="border-t border-slate-50">
                          <td className="py-1.5 font-medium text-slate-800">{i.item}</td>
                          <td className="font-mono">{i.qty}</td>
                          <td className="text-slate-500">{i.unit}</td>
                          <td className="text-slate-500">{i.notes}</td>
                          <td className="text-slate-400">{i.section}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
